/**
 * Display formatting (sizes, durations, dates, numbers). Locale-aware via `Intl` with the
 * browser locale; pass `locale` explicitly in tests.
 */

type DateInput = string | number | Date;

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

const BYTE_UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB'] as const;

/** `1536` → `"1.5 KiB"`, `692_000_000` → `"660 MiB"` (IEC units, 3 significant digits). */
export function formatBytes(bytes: number, locale?: string): string {
  if (!Number.isFinite(bytes)) return '–';
  const sign = bytes < 0 ? '-' : '';
  let value = Math.abs(bytes);
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = unit === 0 || value >= 100 ? 0 : value >= 10 ? 1 : 2;
  const number = value.toLocaleString(locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
  return `${sign}${number} ${BYTE_UNITS[unit]}`;
}

/** Transfer rate, e.g. `"12.4 MiB/s"`. */
export function formatRate(bytesPerSecond: number, locale?: string): string {
  return `${formatBytes(bytesPerSecond, locale)}/s`;
}

/**
 * Human duration of `seconds`: `"450 ms"`, `"12.3 s"`, `"4 min 05 s"`, `"2 h 07 min"`,
 * `"3 d 4 h"`. Negative / non-finite → `"–"`.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '–';
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)} s`;
  const total = Math.round(seconds);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d} d ${h} h`;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`;
  return `${m} min ${String(s).padStart(2, '0')} s`;
}

/** Elapsed time from `start` to `end` (`null`/omitted = now), e.g. a job's run time. */
export function formatElapsed(start: DateInput, end: DateInput | null = null): string {
  const stop = end === null ? Date.now() : toDate(end).getTime();
  return formatDuration((stop - toDate(start).getTime()) / 1000);
}

const RELATIVE_STEPS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.34524],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
];

/** `"just now"`, `"5 minutes ago"`, `"in 2 days"`, … relative to `now`. */
export function formatRelative(value: DateInput, now: DateInput = Date.now(), locale?: string): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '–';
  let delta = (date.getTime() - toDate(now).getTime()) / 1000;
  if (Math.abs(delta) < 45) return 'just now';
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of RELATIVE_STEPS) {
    if (Math.abs(delta) < size) return rtf.format(Math.round(delta), unit);
    delta /= size;
  }
  return rtf.format(Math.round(delta), 'year');
}

/** Absolute date + time, e.g. `"28 Sep 2026, 14:03"`. */
export function formatDateTime(value: DateInput, locale?: string): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '–';
  return date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Absolute date, e.g. `"28 Sep 2026"`. */
export function formatDate(value: DateInput, locale?: string): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '–';
  return date.toLocaleDateString(locale, { dateStyle: 'medium' });
}

/** Grouped number with at most `maximumFractionDigits` decimals (default 2). */
export function formatNumber(value: number, maximumFractionDigits = 2, locale?: string): string {
  if (!Number.isFinite(value)) return '–';
  return value.toLocaleString(locale, { maximumFractionDigits });
}

/** Fraction 0..1 → `"42%"` (or with `digits` decimals). */
export function formatPercent(fraction: number, digits = 0, locale?: string): string {
  if (!Number.isFinite(fraction)) return '–';
  return (
    (fraction * 100).toLocaleString(locale, {
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    }) + '%'
  );
}

/** `"in_progress"` → `"In progress"` (enum values in the UI). */
export function humanize(value: string): string {
  const text = value.replace(/[_-]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
